import { useCallback } from 'react';

import { useCryptoBalance } from '@sorare/core/src/hooks/useCryptoBalance';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

type Args =
  | {
      monetaryAmountToPayWithFiatWallet: MonetaryAmount;
      monetaryAmountToPayWithCryptoWallet: MonetaryAmount;
    }
  | MonetaryAmount;

export const useHasInsufficientFundsInWallets = () => {
  const { availableBalanceInCents: fiatAvailableBalance, fiatCurrency } =
    useFiatBalance();
  const { availableBalance: cryptoAvailableBalance, cryptocurrency } =
    useCryptoBalance();

  const diff = (balance: bigint | string, weiAmountToPay: bigint) => {
    // We are force to cast the availableBalance as a bigint before comparing
    // to avoid some weird case when apollo reads directly the cache and
    // is not using the scalars function to cast the WeiAmount type into a BigInt
    // https://github.com/apollographql/apollo-client/issues/9293
    const bigIntBalance =
      typeof balance === 'string' ? BigInt(balance) : balance;
    return weiAmountToPay - bigIntBalance;
  };

  return useCallback(
    (args: Args) => {
      const monetaryAmountToPayWithCryptoWallet =
        'monetaryAmountToPayWithCryptoWallet' in args
          ? args.monetaryAmountToPayWithCryptoWallet
          : args;
      const monetaryAmountToPayWithFiatWallet =
        'monetaryAmountToPayWithFiatWallet' in args
          ? args.monetaryAmountToPayWithFiatWallet
          : args;

      const insufficientFundsInCryptoWallet =
        cryptoAvailableBalance <
        monetaryAmountToPayWithCryptoWallet.cryptoSubUnit(cryptocurrency);
      const diffInSubUnitForCryptoWallet =
        (insufficientFundsInCryptoWallet &&
          diff(
            cryptoAvailableBalance,
            monetaryAmountToPayWithCryptoWallet.cryptoSubUnit(cryptocurrency)
          )) ||
        undefined;
      const insufficientFundsInFiatWallet =
        fiatAvailableBalance <
        monetaryAmountToPayWithFiatWallet.readAmount(fiatCurrency);
      const diffInFiatCentsForFiatWallet =
        (insufficientFundsInFiatWallet &&
          fiatAvailableBalance -
            monetaryAmountToPayWithFiatWallet.readAmount(fiatCurrency)) ||
        undefined;
      return {
        insufficientFundsInCryptoWallet,
        diffInSubUnitForCryptoWallet,
        insufficientFundsInFiatWallet,
        diffInFiatCentsForFiatWallet,
      };
    },
    [cryptoAvailableBalance, fiatAvailableBalance, fiatCurrency, cryptocurrency]
  );
};
